/**
 * Section: Site header
 * ------------------------------------------------------------------------------
 * Contains highly coupled scripts to control site header components
 *
 * @namespace siteHeader
 */
 import Cookies from 'js-cookie';
 import {register} from '@shopify/theme-sections';

 import {disableBodyScroll, enableBodyScroll} from 'body-scroll-lock';
 import * as a11y from '@shopify/theme-a11y';
 import cssClasses from '../helpers/cssClasses';
 import breakpoints from '../helpers/breakpoints';
 import {debounce, on, getSiblings} from '../helpers/utils';
 import Toggle from '../components/toggle';
 import Notification from '../components/notification';
 import ScrollDirection from '../mixins/scroll-direction';
 import ScrollBar from '~/components/feature-carousel';

 /**
  * DOM selectors.
  */
 const selectors = {
   wrapper: '[js-site-header="wrapper"]',
   overlay: '[js-site-header="overlay"]',
   container: '[js-site-header="container"]',
   historyBack: '[js-site-header="back"]',
   megaNav: '[js-site-header="megaNav"]',
   megaNavToggle: '[js-site-header="megaNavToggle"]',
   megaNavClose: '[js-site-header="close"]',
   siteNav: 'js-site-nav',
   siteNavItem: '[js-site-header="siteNavItem"]',
   siteNavLink: '[js-site-header="siteNavLink"]',
   subNav: '[js-site-header="subNav"]',
   navLink: '[js-site-header="navLink"]',
   childLink: '[js-site-nav="child"]',
   storeIcon: '[js-site-header="storeIcon"]',
   searchTrigger: '[js-toggle="searchBarOverlay"]',
   topBarLink: '[js-header="topLink"]',
   loginLink: '[js-header="login"]',
   searchBarOverlay: '[js-search="overlay"]',
 };

 /**
  * Instance cookies.
  */
 const cookieSelector = {
   geolocation: 'redirect_geolocation_v3',
   settings: 'redirect_store_settings_v3',
 };

 /**
  * Data selectors.
  */
 const dataSelectors = {
   meganav: 'data-meganav',
   children: 'data-children',
 };

 const hideMegaNavTimer = 500;
 let blurTimer;

 /**
  * Create a new site header object.
  */
 register('site-header', {

   /**
    * Callback function when the section is loaded in via the 'sections.load()' function or by Theme Editor
    * 'shopify:section:unload' event
    */
   onLoad() {
     this.constructComponents();
     this.init();
   },

   /**
    * Component selectors.
    */
   setNodeSelectors() {
     this.wrapper = document.querySelector(selectors.wrapper);
     this.overlay = document.querySelector(selectors.overlay);
     this.container = document.querySelector(selectors.container);
     this.historyBack = document.querySelector(selectors.historyBack);
     this.megaNav = [...document.querySelectorAll(selectors.megaNav)];
     this.megaNavToggle = [...document.querySelectorAll(selectors.megaNavToggle)];
     this.megaNavClose = [...document.querySelectorAll(selectors.megaNavClose)];
     this.navLink = [...document.querySelectorAll(selectors.navLink)];
     this.siteNavItem = [...document.querySelectorAll(selectors.siteNavItem)];
     this.childLink = [...document.querySelectorAll(selectors.childLink)];
     this.storeIcon = document.querySelector(selectors.storeIcon);
     this.searchTrigger = [...document.querySelectorAll(selectors.searchTrigger)];
     this.subNav = [...document.querySelectorAll(selectors.subNav)];
     this.topBarLink = [...document.querySelectorAll(selectors.topBarLink)];
     this.loginLink = document.querySelector(selectors.loginLink);
   },

   /**
    * Component variables.
    */
   setVariables() {
     this.blurTimer = blurTimer;
     this.hideMegaNavTimer = hideMegaNavTimer;
     this.ariaExpandedString = 'aria-expanded';
   },


   /**
    * Instantiate search bar toggle.
    */
   constructSearchToggle() {
     this.SearchBar = Toggle({
       toggleSelector: 'searchBarOverlay',
       exclude: [
         '[data-target="searchBarOverlay"]',
       ],
     });
   },

   /**
    * Instantiate announcement component.
    */
   constructAnnouncement() {
     this.Announcement = Notification({
       container: '.site-header__announcement',
       enableCookies: true,
       cookie: {
         namespace: 'frame_announcement',
         value: 'dismissed',
       },
     });
   },

   /**
    * Instantiate a collapsed header on scroll using scrollDirection helper.
    */
   constructStickyHeader() {
     this.AnimatedHeader = ScrollDirection({
       throttle: 250,
       start: 100,
     });
   },

   /**
    * Construct section components.
    */
   constructComponents() {
     this.constructSearchToggle();
     this.constructAnnouncement();
     this.constructStickyHeader();
     ScrollBar().init();
   },

   /**
    * Initialise component.
    */
   init() {
     this.setNodeSelectors();
     this.setEventListeners();
     this.setEventBusListeners();
     this.setStickyHeader();
     this.setSearchToggle();
     this.handleCloseClick();
   },

   /**
    * Set search toggle events.
    */
   setSearchToggle() {
     this.SearchBar.init();
   },

   /**
    * Set sticky header events after document has loaded styling.
    */
   setStickyHeader() {
     on('load', () => {
       this.AnimatedHeader.init();
       on('resize', debounce(() => this.handleResizeEvents(), 250));
     });
   },

   /**
    * Get store icon using cookies and other stored information.
    */
   getStoreIcon() {
     const storeSettings = Cookies.getJSON(cookieSelector.settings);
     const storeCode = (typeof storeSettings === 'undefined' ? theme.store.storeCode : storeSettings.loc);
     return storeCode.toLowerCase();
   },

   /**
    * Update the icon based on the user's store settings.
    * @
    */
   setStoreIcon(countryCode = this.getStoreIcon()) {
     this.storeIcon.className = `flag-icon flag-icon-${countryCode}`;
   },

   /**
    * Handle resize events by requesting animation frame.
    */
   handleResizeEvents() {
     this.hideAllMegaNavs();
   },

   /**
    * Cancels the blur timer if the focus is still on a Nav Link item
    * @param {HTMLElement} element - The Mega Nav link element
    */
   setKeyFocus(element) {
     on('focus', element, () => window.clearTimeout(this.blurTimer));
   },

   /**
    * Sets a timer that hides the Mega Nav if no Site Nav element is focused
    * @param {HTMLElement} element - The Mega Nav link element
    */
   setKeyBlur(element) {
     const siteNavParent = this.getSiteNavParent(element);
     on('blur', element, () => {
       this.blurTimer = window.setTimeout(() => this.hideMegaNav(siteNavParent), hideMegaNavTimer);
     });
   },

   /**
    * Hides active sibling Mega Nav items and sets aria-expanded to false using hideMegaNav function
    * @param {HTMLElement} element - The Mega Nav toggle button
    */
   hideSiblingMegaNavs(element) {
     const siteHeader = element.parentNode;
     siteHeader.childNodes.forEach((child) => {
       if (child !== element && child.nodeType === 1 && child.classList.contains(cssClasses.active)) {
         this.hideMegaNav(child);
       }
       return element;
     });
   },

   /**
    * Displays a Mega Nav item and sets aria-expanded to true
    * @param {HTMLElement} siteNavParent - The Mega Nav
    */
   showMegaNav(siteNavParent) {
     const currentNavParent = siteNavParent.dataset.parent;
     siteNavParent.classList.add(cssClasses.active);
     siteNavParent.querySelector(selectors.megaNavToggle).setAttribute(this.ariaExpandedString, true);
     siteNavParent.querySelector(`[data-meganav="${currentNavParent}"]`).classList.add(cssClasses.active);
     document.documentElement.classList.add('meganav-open');

     this.toggleMegaNavItems();

   },

   /**
    * Hides a Mega Nav item and sets aria-expanded to false
    * @param {HTMLElement} siteNavParent - The Mega Nav
    */
   hideMegaNav(siteNavParent) {
     const currentNavParent = siteNavParent.dataset.parent;
     siteNavParent.classList.remove(cssClasses.active);
     siteNavParent.querySelector(selectors.megaNavToggle).setAttribute(this.ariaExpandedString, false);
     siteNavParent.querySelector(`[data-meganav="${currentNavParent}"]`).classList.remove(cssClasses.active);
     this.hideSubNav();


     document.documentElement.classList.remove('meganav-open');

     if (window.matchMedia(`(min-width: ${breakpoints.large})`).matches) {
       Frame.EventBus.emit('Toggle:menuDrawer:close');
     }
   },

   /**
    * Hides all open mega-navigations
    * @param {Boolean} disable - Disable Mega Nav transition.
    */
   hideAllMegaNavs(disable) {
     this.megaNavToggle.forEach((element) => {
       const siteNavParent = this.getSiteNavParent(element);

       if (disable) {
         if (!element.classList.contains('meganav__link')) {
           element.nextElementSibling.classList.add(cssClasses.removing);
         }

         this.hideMegaNav(siteNavParent);

         if (!element.classList.contains('meganav__link')) {
           window.setTimeout(() => {
             element.nextElementSibling.classList.remove(cssClasses.removing);
           }, 600);
         }

         return;
       }

       this.hideMegaNav(siteNavParent);
     });
   },

   /**
    * Selects the siteNav parent
    * @param {HTMLElement} element - The Nav List item or the megaNavToggle button
    */
   getSiteNavParent(element) {
     if (element.classList.contains('meganav__link')) {
       const data = element.getAttribute(dataSelectors.meganav);
       return document.querySelector(`[data-parent="${data}"]`);
     }

     return element.closest(selectors.siteNavItem);
   },

   /**
    * Toggle Mega Nav, update aria tags and hide other Mega Nav items
    * @param {HTMLElement} element - The Nav List item or the Mega Nav Toggle button
    */
   toggleMegaNavItems() {
     this.navLink.forEach((element) => {
       on('mouseover', element, (event) => {
         this.handleToggleMegaNav(element, event);
       });

       on('touchstart', element, (event) => {
         if (!element.hasAttribute(dataSelectors.children) || element.attributes.href.value !== '#') {
           element.click();
           return;
         }

         this.handleToggleMegaNav(element, event);
       });

       on('focus', element, (event) => {
         this.handleToggleMegaNav(element, event);
       });
     });

     this.childLink.forEach((element) => {
       on('click', element, () => {
         this.dataLayerPush(element);
       });

       on('touchstart', element, () => {
         this.dataLayerPush(element);
         element.click();
       });
     });
   },

   /**
    * Handle toggle meganav.
    * @param {Node} element toggeled element.
    * @param {Event} event toggle event.
    */
   handleToggleMegaNav(element) {
     if (!element.hasAttribute(dataSelectors.children)) {
       this.hideSubNav();
       return;
     }

     const list = element.getAttribute(dataSelectors.children);
     const parent = document.querySelector(`[data-parent="${list}"]`).parentNode.parentNode;
     const listSiblings = getSiblings(element.parentNode);

     element.classList.add(cssClasses.active);

     listSiblings.forEach((listItem) => {
       listItem.querySelector(selectors.navLink).classList.remove(cssClasses.active);
     });

     this.showSubNav();
     this.handleMegaNavList(list, parent);
   },

   /**
    * Push to DataLayer
    * @param {Node} element clicked element.
    */
   dataLayerPush(element) {
     const navListParent = element.closest('.navlist');
     const secondLevel = (navListParent) ? navListParent.dataset.parent : 'not_selected';

     Frame.EventBus.emit('Datalayer:push', {
       event: 'GAevent',
       eventID: '01',
       eventCategory: 'menu',
       eventAction: secondLevel,
       eventLabel: element.dataset.handle,
     });
   },

   /**
    * Show secondary navigation
    */
   showSubNav() {
     document.documentElement.classList.add('subnav-open');

     this.subNav.forEach((element) => {
       element.classList.add(cssClasses.active);
     });
   },

   /**
    * Hide secondary navigation
    */
   hideSubNav() {
     document.documentElement.classList.remove('subnav-open');

     this.subNav.forEach((element) => {
       const links = [...document.querySelectorAll(selectors.navLink)];
       element.classList.remove(cssClasses.active);

       links.forEach((link) => {
         link.classList.remove(cssClasses.active);
       });
     });
   },

   /**
    * Handle display of list on meganav item toggle.
    * @param {obj} list
    * @param {node} parent
    */
   handleMegaNavList(list, parent) {
     const childList = parent.querySelector(`[data-parent="${list}"]`);
     const siblingLists = parent.querySelectorAll(`[data-parent]:not([data-parent="${list}"])`);

     childList.classList.add(cssClasses.active);

     siblingLists.forEach((listItem) => {
       listItem.classList.remove(cssClasses.active);
     });
   },

   /**
    * Handle meganav close.
    */
   handleCloseClick() {
     this.megaNavClose.forEach((element) => {
       on('click', element, (event) => {
         event.preventDefault();
         this.hideAllMegaNavs();

         if (window.matchMedia(`(min-width: ${breakpoints.large})`).matches) {
           Frame.EventBus.emit('Toggle:menuDrawer:close');
         }
       });
     });
   },

   /**
    * Check if menu-drawer is already open.
    */
   checkDrawerOpen() {
     let isActive = false;

     this.siteNavItem.forEach((element) => {
       if (element.classList.contains(cssClasses.active)) { isActive = true; }
     });

     return isActive;
   },

   /**
    * Handle navigation toggle.
    * @param {Node} element toggle
    * @param {Event} event trigger event
    */
   handleNavToggle(element, event) {
     const siteNavParent = this.getSiteNavParent(element);

     if (siteNavParent.classList.contains(cssClasses.active)) {
       return;
     }

     const drawerActive = this.checkDrawerOpen();

     event.preventDefault();
     if (drawerActive) {
       this.hideAllMegaNavs(true);
     }

     this.showMegaNav(siteNavParent);


     if (window.matchMedia(`(min-width: ${breakpoints.large})`).matches) {
       Frame.EventBus.emit('Toggle:menuDrawer:open');
     }
   },

   /**
    * Set event listeners.
    */
   setEventListeners() {
     this.megaNavToggle.forEach((element) => {
       on('click', element, (event) => {
         this.handleNavToggle(element, event);
       });

       on('mouseover', element, (event) => {
         this.hideSubNav();

         if (element.classList.contains('meganav__link')) {
           this.handleNavToggle(element, event);
         }
       });

       on('touchstart', element, (event) => {
         this.hideSubNav();

         if (element.classList.contains('meganav__link')) {
           this.handleNavToggle(element, event);
         }
       });

       on('focus', element, (event) => {
         this.hideSubNav();

         if (element.classList.contains('meganav__link')) {
           this.handleNavToggle(element, event);
         }
       });
     });

     this.navLink.forEach((element) => {
       this.setKeyBlur(element);
       this.setKeyFocus(element);
     });

     on('click', this.historyBack, () => {
       history.back();
     });

     on('click', this.overlay, () => {
       this.hideAllMegaNavs(true);
     });

     this.topBarLink.forEach((item) => {
       on('click', item, () => {
         const value = item.href.split('/').pop();

         Frame.EventBus.emit('Datalayer:push', {
           event: 'GAevent',
           eventID: '02',
           eventCategory: 'header',
           eventAction: (value.length > 0) ? value : 'logo',
           eventLabel: 'click',
         });
       });
     });

     if (this.loginLink) {
       on('click', this.loginLink, () => {
         window.dataLayer.push({
           event: 'GAevent',
           eventID: '08',
           eventCategory: 'account',
           eventAction: 'login',
           eventLabel: 'step1',
         });
       });
     }
   },



   /**
    * Set event bus listeners.
    */
   setEventBusListeners() {
     Frame.EventBus.listen('Notification:dismissed', () => {
       this.updateBodyOffset();
     });

     Frame.EventBus.listen('ScrollDirection:down', () => {
       this.wrapper.classList.add(cssClasses.collapsed);
       this.container.parentNode.classList.add('inverse-bg-colourway');

       if (this.searchBarOverlay) {
         Frame.EventBus.emit('Overlay:searchBarOverlay:close');
       }
     });

     Frame.EventBus.listen([
       'ScrollDirection:up',
       'ScrollDirection:paused',
       'ScrollDirection:top',
     ], (response) => {

       if (response === 'at-top') {
         this.container.parentNode.classList.remove('inverse-bg-colourway');
       }

       this.wrapper.classList.remove(cssClasses.collapsed);
     });

     Frame.EventBus.listen('Toggle:searchBarOverlay:open', () => {
      document.getElementById('searchBarOverlay').classList.remove(cssClasses.hidden);
       document.documentElement.classList.add('js-search-active');
       disableBodyScroll();
       this.hideAllMegaNavs();
     });

     Frame.EventBus.listen('Toggle:searchBarOverlay:close', () => {
      document.getElementById('searchBarOverlay').classList.add(cssClasses.hidden);
      document.getElementById('Search').value = '';
       enableBodyScroll();
       document.documentElement.classList.remove('js-search-active');
       document.querySelector('[js-search-bar="fixed-content"]').classList.remove(cssClasses.hidden)
       document.getElementById('predictive-search-results').classList.add(cssClasses.hidden)
     });

     Frame.EventBus.listen(['EscEvent:on'], () => {
       this.hideAllMegaNavs();
     });
   },
 });
